import {
  Activity,
  AlertCircle,
  BarChart3,
  Bot,
  CheckCircle,
  Coins,
  Database,
  FileText,
  MessageSquare,
  RefreshCw,
  Users,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Badge,
    Button,
    BuyTokensModal,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    LoadingSpinner,
} from '../components/ui';
import { useOrganization, usePage } from '../contexts';
import { useTokens } from '../hooks';
import {
    agentService,
    crewService,
    knowledgeSourceService,
    taskService,
} from '../services';
import { Agent, Crew, KnowledgeSource } from '../types';

interface DashboardStats {
  totalCrews: number;
  activeCrews: number;
  totalAgents: number;
  totalTasks: number;
  totalKnowledgeSources: number;
  processedKnowledgeSources: number;
  pendingKnowledgeSources: number;
  errorKnowledgeSources: number;
  totalFileSize: number;
  totalEmbeddings: number;
}

interface RecentActivity {
  id: string;
  type: 'crew' | 'agent' | 'knowledge';
  name: string;
  description: string;
  timestamp: Date;
  status: 'success' | 'warning' | 'error';
}

const Dashboard: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const { setPageInfo } = usePage();
  const { t } = useTranslation();
  const { openBuyTokensModal, closeBuyTokensModal, isBuyTokensModalOpen } =
    useTokens();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentCrews, setRecentCrews] = useState<Crew[]>([]);
  const [recentAgents, setRecentAgents] = useState<Agent[]>([]);
  const [recentKnowledgeSources, setRecentKnowledgeSources] = useState<
    KnowledgeSource[]
  >([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const loadDashboardData = useCallback(async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const [
        crewsData,
        activeCrewsData,
        agentsData,
        tasksData,
        knowledgeSourcesData,
      ] = await Promise.all([
        crewService.getAll({
          organization_id: currentOrganization.id,
          page: 1,
          page_size: 5,
        }),
        crewService.getAll({
          organization_id: currentOrganization.id,
          is_active: true,
          page: 1,
          page_size: 1,
        }),
        agentService.getAll({
          organization_id: currentOrganization.id,
          page: 1,
          page_size: 5,
        }),
        taskService.getAll({
          organization_id: currentOrganization.id,
          page: 1,
          page_size: 1,
        }),
        knowledgeSourceService.getAll({
          organization_id: currentOrganization.id,
          page: 1,
          page_size: 5,
        }),
      ]);

      const crews = crewsData.items || [];
      const agents = agentsData.items || [];
      const knowledgeSources = knowledgeSourcesData.items || [];
      const totalKnowledgeSources = knowledgeSourcesData.total || 0;
      const processedKnowledgeSources = knowledgeSources.filter(
        source => source.processing_status === 'completed'
      ).length;
      const pendingKnowledgeSources = knowledgeSources.filter(
        source => source.processing_status === 'pending'
      ).length;
      const errorKnowledgeSources = knowledgeSources.filter(
        source => source.processing_status === 'error'
      ).length;
      const totalFileSize = knowledgeSources.reduce(
        (sum, source) => sum + (source.file_size || 0),
        0
      );
      const totalEmbeddings = knowledgeSources.reduce(
        (sum, source) => sum + (source.embeddings_count || 0),
        0
      );

      setStats({
        totalCrews: crewsData.total || 0,
        activeCrews: activeCrewsData.total || 0,
        totalAgents: agentsData.total || 0,
        totalTasks: tasksData.total || 0,
        totalKnowledgeSources,
        processedKnowledgeSources,
        pendingKnowledgeSources,
        errorKnowledgeSources,
        totalFileSize,
        totalEmbeddings,
      });

      // Dados recentes (últimos 5)
      setRecentCrews(crews);
      setRecentAgents(agents);
      setRecentKnowledgeSources(knowledgeSources);

      const crewActivity: RecentActivity[] = crews.map(crew => ({
        id: `crew-${crew.id}`,
        type: 'crew',
        name: crew.name,
        description: t('dashboard.activityCrewUpdated'),
        timestamp: new Date(crew.updated_at || crew.created_at),
        status: crew.is_active ? 'success' : 'warning',
      }));
      const agentActivity: RecentActivity[] = agents.map(agent => ({
        id: `agent-${agent.id}`,
        type: 'agent',
        name: agent.name,
        description: t('dashboard.activityAgentUpdated'),
        timestamp: new Date(agent.updated_at || agent.created_at),
        status: agent.is_active ? 'success' : 'warning',
      }));
      const knowledgeActivity: RecentActivity[] = knowledgeSources.map(source => ({
        id: `knowledge-${source.id}`,
        type: 'knowledge',
        name: source.name,
        description:
          source.processing_status === 'completed'
            ? t('dashboard.activityKnowledgeProcessed')
            : t('dashboard.activityKnowledgeUpdated'),
        timestamp: new Date(source.updated_at || source.created_at),
        status:
          source.processing_status === 'error'
            ? 'error'
            : source.processing_status === 'completed'
            ? 'success'
            : 'warning',
      }));

      const mergedActivity = [...crewActivity, ...agentActivity, ...knowledgeActivity]
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 8);
      setRecentActivity(mergedActivity);

      setLastUpdated(new Date());
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, t]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Set page title and description
  useEffect(() => {
    setPageInfo(t('dashboard.title'), t('dashboard.subtitle'));
  }, [setPageInfo, t]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActivityTypeIcon = (type: string) => {
    switch (type) {
      case 'crew':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'agent':
        return <Bot className="h-4 w-4 text-purple-500" />;
      case 'task':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'knowledge':
        return <Database className="h-4 w-4 text-orange-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Last Updated Info */}
      <div className="flex items-center justify-end">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.lastUpdate')}: {lastUpdated.toLocaleTimeString()}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openBuyTokensModal}
            className="flex items-center space-x-2"
          >
            <Coins className="h-4 w-4" />
            <span>{t('tokens.buyTokens')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadDashboardData}
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>{t('dashboard.update')}</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.teams')}
            </CardTitle>
            <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.totalCrews || 0}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {stats?.activeCrews || 0} {t('dashboard.activeTeams')}
            </p>
            <div className="mt-2">
              <Badge
                variant={
                  stats?.activeCrews && stats.activeCrews > 0
                    ? 'success'
                    : 'error'
                }
              >
                {stats?.activeCrews && stats.activeCrews > 0
                  ? t('dashboard.active')
                  : t('dashboard.inactive')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.agents')}
            </CardTitle>
            <Bot className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.totalAgents || 0}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.configuredAgents')}
            </p>
            <div className="mt-2">
              <Badge variant="secondary">
                {stats?.totalTasks || 0} {t('dashboard.tasks')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.tasks')}
            </CardTitle>
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.totalTasks || 0}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('dashboard.configuredTasks')}
            </p>
            <div className="mt-2">
              <Badge variant="secondary">
                {stats?.totalAgents || 0} {t('dashboard.agents')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900 dark:text-white">
              {t('dashboard.knowledgeBase')}
            </CardTitle>
            <Database className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.totalKnowledgeSources || 0}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {stats?.processedKnowledgeSources || 0} {t('dashboard.processed')}
            </p>
            <div className="mt-2">
              <Badge variant="secondary">
                {formatFileSize(stats?.totalFileSize || 0)}
              </Badge>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Recent Activity and Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-gray-900 dark:text-white">
              <Activity className="h-5 w-5" />
              <span>{t('dashboard.recentActivity')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map(activity => (
                  <div
                    key={activity.id}
                    className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    {getActivityTypeIcon(activity.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {activity.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {activity.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(activity.status)}
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {activity.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p>{t('dashboard.noRecentActivity')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-gray-900 dark:text-white">
              <BarChart3 className="h-5 w-5" />
              <span>{t('dashboard.systemHealth')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('dashboard.activeTeams')}
              </span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{
                      width: `${
                        stats ? (stats.activeCrews / stats.totalCrews) * 100 : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {stats
                    ? Math.round((stats.activeCrews / stats.totalCrews) * 100)
                    : 0}
                  %
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('dashboard.processedKnowledge')}
              </span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{
                      width: `${
                        stats && stats.totalKnowledgeSources > 0
                          ? (stats.processedKnowledgeSources /
                              stats.totalKnowledgeSources) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {stats && stats.totalKnowledgeSources > 0
                    ? Math.round(
                        (stats.processedKnowledgeSources /
                          stats.totalKnowledgeSources) *
                          100
                      )
                    : 0}
                  %
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('dashboard.embeddings')}
              </span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{
                      width: `${
                        stats && stats.totalKnowledgeSources > 0
                          ? Math.min(
                              100,
                              (stats.processedKnowledgeSources /
                                stats.totalKnowledgeSources) *
                                100
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {stats?.totalEmbeddings || 0}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('dashboard.erroredKnowledge')}
              </span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full"
                    style={{
                      width: `${
                        stats && stats.totalKnowledgeSources > 0
                          ? (stats.errorKnowledgeSources / stats.totalKnowledgeSources) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {stats?.errorKnowledgeSources || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Items */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              {t('dashboard.recentTeams')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentCrews.length > 0 ? (
              <div className="space-y-3">
                {recentCrews.map(crew => (
                  <div
                    key={crew.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/agent-teams"
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {crew.name}
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {crew.description}
                      </p>
                    </div>
                    <Badge variant={crew.is_active ? 'success' : 'error'}>
                      {crew.is_active
                        ? t('dashboard.active')
                        : t('dashboard.inactive')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p>{t('dashboard.noTeamsFound')}</p>
                <Button
                  className="mt-4"
                  onClick={() => (window.location.href = '/agent-teams')}
                >
                  {t('dashboard.createFirstTeam')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              {t('dashboard.knowledgeBase')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentKnowledgeSources.length > 0 ? (
              <div className="space-y-3">
                {recentKnowledgeSources.map(source => (
                  <div
                    key={source.id}
                    className="flex items-start justify-between gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/knowledge-base"
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline block truncate"
                      >
                        {source.name}
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1">
                        {source.description}
                      </p>
                      <div className="mt-2">
                        <Badge variant="secondary">{source.file_type}</Badge>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {source.embeddings_count || 0}{' '}
                        {t('dashboard.embeddings')}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {Math.round(source.file_size / 1024)} KB
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Database className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p>{t('dashboard.noKnowledgeSources')}</p>
                <Button
                  className="mt-4"
                  onClick={() => (window.location.href = '/knowledge-base')}
                >
                  {t('dashboard.addKnowledge')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              {t('dashboard.recentAgents')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentAgents.length > 0 ? (
              <div className="space-y-3">
                {recentAgents.map(agent => (
                  <div
                    key={agent.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/agent-teams"
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {agent.name}
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {agent.role}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2 bg-transparent">
                        {agent.goal}
                      </p>
                    </div>
                    <Badge variant={agent.is_active ? 'success' : 'error'}>
                      {agent.is_active
                        ? t('dashboard.active')
                        : t('dashboard.inactive')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p>{t('dashboard.noAgentsFound')}</p>
                <Button
                  className="mt-4"
                  onClick={() => (window.location.href = '/agent-teams')}
                >
                  {t('dashboard.createFirstAgent')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.quickActions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Button
              className="h-20 flex flex-col items-center justify-center space-y-2"
              onClick={() => (window.location.href = '/conversations')}
            >
              <MessageSquare className="h-6 w-6" />
              <span>{t('dashboard.newConversation')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center space-y-2"
              onClick={() => (window.location.href = '/agent-teams')}
            >
              <Users className="h-6 w-6" />
              <span>{t('dashboard.createTeam')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center space-y-2"
              onClick={() => (window.location.href = '/knowledge-base')}
            >
              <Database className="h-6 w-6" />
              <span>{t('dashboard.addKnowledge')}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Buy Tokens Modal */}
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />
    </div>
  );
};

export default Dashboard;
